import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, input, viewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { SharedFormViewComponent } from 'src/app/pages/_templates/shared-form-view/shared-form-view.component';
import { SharedFieldTypeEnum } from 'src/app/shared/models/shared/shared-field-type.enum';
import { SharedSectionI } from 'src/app/shared/models/shared/shared-section.interface';
import { valuePairToValue } from '@softpak/components/spx-helpers';
import { sharedContainerSearchActions } from 'src/app/store/shared-container-search';
import { ctrlCassetteCode, ctrlContainerNumber, ctrlContainerPrefix, sectionContainerNumber } from '../../_config/ctrls/ctrls-constants';
import { spValRequired } from '../../validators';
import { SharedSearchChildContextI } from './shared-search-child-context.interface';

@Component({
  selector: 'shared-search-casette',
  templateUrl: './shared-search-casette.component.html',
  imports: [SharedFormViewComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedSearchCasetteComponent implements OnInit, OnDestroy {
  private readonly appStore = inject(Store);

  formGroup = input.required<FormGroup>();
  context = input.required<SharedSearchChildContextI>();

  private readonly formView = viewChild<SharedFormViewComponent>('formView');
  private readonly ctrlCassetteCode = computed(
    () => this.formGroup().get(ctrlCassetteCode) as FormControl | null
  );
  private readonly ctrlContainerNumber = computed(
    () => this.formGroup().get(ctrlContainerNumber) as FormControl | null
  );
  private readonly ctrlContainerPrefix = computed(
    () => this.formGroup().get(ctrlContainerPrefix) as FormControl | null
  );

  readonly sections: SharedSectionI[] = [
    {
      key: sectionContainerNumber,
      index: 2,
      show: () => true,
      showTitle: () => false,
      fields: [
        {
          key: ctrlCassetteCode,
          type: SharedFieldTypeEnum.overlayNumeric,
          entity: undefined,
          defaultValue: null,
          validators: [spValRequired()],
          readonly: () => false,
          suggestions: () => false,
          afterValueChange: undefined,
          foreignKeys: undefined,
          show: () => true,
          capitalize: true,
          autofocus: () => true,
        },
      ],
    },
  ];

  private readonly subscriptions: {
    cassetteInput?: Subscription;
  } = {};

  ngOnInit(): void {
    this.configureCasetteMode();
    this.listenToCassetteCodeInput();
  }

  ngOnDestroy(): void {
    this.subscriptions.cassetteInput?.unsubscribe();
  }

  triggerChangeDetection(): void {
    this.formView()?.triggerChangeDetection();
  }

  applyFocus(fieldName: string): void {
    this.formView()?.applyFocus(fieldName);
  }

  applyAutofocus(): void {
    this.formView()?.applyAutofocus();
  }

  private listenToCassetteCodeInput(): void {
    this.subscriptions.cassetteInput?.unsubscribe();
    const cassetteCodeControl = this.ctrlCassetteCode();
    if (!cassetteCodeControl) {
      return;
    }

    this.subscriptions.cassetteInput = cassetteCodeControl.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged((a, b) => valuePairToValue(a) === valuePairToValue(b))
    ).subscribe((valuePair) => {
      const value = valuePairToValue(valuePair);
      if (value) {
        this.appStore.dispatch(sharedContainerSearchActions.load({
          pageType: this.context().pageType,
          query: {
            ...this.context().searchParameters,
            mafiCode: value,
            recordType: this.context().recordType,
            inQuayjobId: this.context().inQuayjobId,
            isCassetteSearch: true,
          }
        }));
      }
    });
  }

  private configureCasetteMode(): void {
    const cassetteCodeControl = this.ctrlCassetteCode();
    const containerNumberControl = this.ctrlContainerNumber();
    const containerPrefixControl = this.ctrlContainerPrefix();
    if (!cassetteCodeControl || !containerNumberControl || !containerPrefixControl) {
      return;
    }

    containerNumberControl.setValidators([]);
    containerPrefixControl.setValidators([]);
    cassetteCodeControl.setValidators([spValRequired()]);

    containerNumberControl.updateValueAndValidity({ emitEvent: false });
    containerPrefixControl.updateValueAndValidity({ emitEvent: false });
    cassetteCodeControl.updateValueAndValidity({ emitEvent: false });

    setTimeout(() => {
      this.applyFocus(ctrlCassetteCode);
    }, 0);
  }
}
